import { FC, forwardRef, useEffect, useState } from 'react';
import { AllieQueryInputProps } from './QueryInput.types';
import { AllieArrowUpCircleIcon } from '../../../styling/icons';
import { AllieItemStyled, QueryInputStyled } from './QueryInput.styles';

export const AllieQueryInput: FC<AllieQueryInputProps> = forwardRef(
  (
    {
      corner = 'rounded-edges',
      borderColor = 'neutral-20',
      onSearch,
      onChange,
      clean,
      initValue,
      rows = 1,
      voiceRecorder,
      enableVoiceRecording = false,
      isRecording = false,
      ...props
    },
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const [value, setValue] = useState(initValue || '');

    useEffect(() => {
      setValue(initValue || '');
    }, [initValue]);

    useEffect(() => {
      if (clean) {
        setValue('');
      }
    }, [clean]);

    const handleIconClick = () => {
      if (props.disabled) {
        return;
      }
      if (onSearch) {
        onSearch(value);
      }
    };

    const handleKeyPress = (
      event: React.KeyboardEvent<HTMLIonInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (props.disabled) {
        return;
      }
      if (
        (event.code === 'Enter' || event.key === 'Enter') &&
        onSearch &&
        event.shiftKey === false
      ) {
        event.preventDefault();
        onSearch(value);
      }
    };

    const handleChange = (
      e: React.ChangeEvent<HTMLIonInputElement> | React.ChangeEvent<HTMLInputElement>
    ) => {
      setValue(e.target.value?.toString() || '');
      onChange?.(e.target.value);
    };

    // Si está grabando, renderizar solo el voiceRecorder con el estilo exacto del input
    if (isRecording && enableVoiceRecording && voiceRecorder) {
      // Determinar el padding vertical según el tamaño del AllieQueryInput
      const verticalPadding = props.allieSize === 'small' ? '8px' : 
                             props.allieSize === 'medium' ? '16px' : '18px'; // normal = 18px
      
      return (
        <div
          style={{
            border: `1.5px solid var(--allie-color-${borderColor})`,
            borderRadius: corner === 'rounded-edges' ? '12px' : '4px',
            background: `var(--allie-color-${props.background || 'neutral-2'})`,
            paddingTop: verticalPadding, // Usar el padding correcto según el tamaño
            paddingBottom: verticalPadding, // Usar el padding correcto según el tamaño
            paddingLeft: '14px', // Mismo que AllieTextArea
            // paddingRight: 'var(--allie-spacing-5xl)', // 40px - para el suffix
            height: '56px', // Forzar altura exacta del textarea
            boxSizing: 'border-box', // Incluir border y padding en la altura
            display: 'flex',
            alignItems: 'center',
            transition: 'border-color 0.2s ease',
            color: 'var(--allie-color-neutral-70)', // mismo color que el textarea
            // ...props.style,
          }}
          onMouseEnter={(e) => {
            if (props.borderColorHover) {
              e.currentTarget.style.borderColor = `var(--allie-color-${props.borderColorHover})`;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = `var(--allie-color-${borderColor})`;
          }}
        >
          {voiceRecorder}
        </div>
      );
    }

    return (
      <QueryInputStyled
        autoGrow={true}
        corner={corner}
        borderColor={borderColor}
        allieSize={props.allieSize || 'normal'}
        suffixAlign="top"
        {...props}
        suffix={
          enableVoiceRecording && voiceRecorder ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {voiceRecorder}
              <AllieItemStyled
                onclick={handleIconClick}
                disabled={!value.length}
                suffixAlign={props.suffixAlign}
                data-testid="fgpt-send-button"
              >
                <div className="icon-wrapper">
                  <AllieArrowUpCircleIcon style={{ display: 'flex' }} />
                </div>
              </AllieItemStyled>
            </div>
          ) : (
            <AllieItemStyled
              onclick={handleIconClick}
              disabled={!value.length}
              suffixAlign={props.suffixAlign}
              data-testid="fgpt-send-button"
            >
              <div className="icon-wrapper">
                <AllieArrowUpCircleIcon style={{ display: 'flex' }} />
              </div>
            </AllieItemStyled>
          )
        }
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        enterkeyhint="search"
        inputmode="text"
        value={value}
        rows={rows}
        ref={ref}
      />
    );
  }
);

AllieQueryInput.displayName = 'AllieQueryInput';
